import React from "react";

interface TrustBuilderScore { surveysReceived: string; sumOfScores: string; avgScore: string; }
interface ClosingSurveyScore { complete: string; onTime: string; customerRatingScore: string; }
interface OverallCombinedSurveyScore { trustBuilders: string; complete: string; onTime: string; customerRatingScore: string; totalAvgScore: string; }
interface SurveysData {
  trustBuilderScore: TrustBuilderScore; closingSurveyScore: ClosingSurveyScore;
  overallCombinedSurveyScore: OverallCombinedSurveyScore; bonusPercentEarned: string; bonusValue: string; score: string;
}
interface SurveysSubFormProps {
  data: SurveysData; isEditable: boolean;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>, options?: { type?: 'number' | 'decimal' | 'text'; maxLength?: number; minLength?: number; max?: number; min?: number }) => void;
}

const SurveysSubForm: React.FC<SurveysSubFormProps> = ({ data, isEditable, onInputChange }) => (
  <div className="sf-section">
    <h3 className="sf-title">Surveys (10% of total achievement score)</h3>
    <p className="sf-subtitle">This will be evaluated on the surveys results from the Closing and Trust Builder surveys.</p>
    <div className="grid grid-cols-2 gap-12">

      {/* Left Column */}
      <div>
        <h4 className="font-semibold mb-2" style={{ color: "var(--text-primary)", fontSize: 14 }}>Trust Builder Score</h4>
        <div className="space-y-2 mb-5">
          <div className="grid grid-cols-2 gap-2 items-center">
            <label className="sf-label" style={{ margin: 0 }}>Surveys Received</label>
            <input type="text" name="customerExperience.surveys.trustBuilderScore.surveysReceived"
              value={data.trustBuilderScore.surveysReceived} inputMode="decimal" maxLength={6}
              onChange={e => onInputChange(e, { type: "decimal", maxLength: 6 })}
              disabled={!isEditable} placeholder="0" onFocus={e => e.target.select()}
              className="sf-input px-2 py-1 text-right text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-2 items-center">
            <label className="sf-label" style={{ margin: 0 }}>Avg Score</label>
            <input type="text" name="customerExperience.surveys.trustBuilderScore.avgScore"
              value={data.trustBuilderScore.avgScore || ""} inputMode="decimal" maxLength={4} min={0} max={5}
              onChange={e => onInputChange(e, { type: "decimal", maxLength: 4 })}
              placeholder="[0.0 - 5.0]" onFocus={e => e.target.select()} disabled={!isEditable}
              className="sf-input px-2 py-1 text-right text-sm" />
          </div>
        </div>

        <h4 className="font-semibold mb-2" style={{ color: "var(--text-primary)", fontSize: 14 }}>Closing Survey Score</h4>
        <div className="space-y-2">
          {[
            { label: "100% Complete", name: "customerExperience.surveys.closingSurveyScore.complete", value: data.closingSurveyScore.complete },
            { label: "On Time", name: "customerExperience.surveys.closingSurveyScore.onTime", value: data.closingSurveyScore.onTime },
            { label: "Customer Rating Score", name: "customerExperience.surveys.closingSurveyScore.customerRatingScore", value: data.closingSurveyScore.customerRatingScore },
          ].map(field => (
            <div key={field.name} className="grid grid-cols-2 gap-2 items-center">
              <label className="sf-label" style={{ margin: 0 }}>{field.label}</label>
              <input type="text" name={field.name} value={field.value || ""} inputMode="decimal" maxLength={6}
                onChange={e => onInputChange(e, { type: "decimal", maxLength: 6 })}
                placeholder="[0.0 - 5.0]" onFocus={e => e.target.select()} disabled={!isEditable}
                className="sf-input px-2 py-1 text-right text-sm" />
            </div>
          ))}
        </div>
      </div>

      {/* Right Column */}
      <div>
        <h4 className="font-semibold mb-2" style={{ color: "var(--text-primary)", fontSize: 14 }}>Overall Combined Survey Score:</h4>
        <div className="space-y-2 mb-4">
          {[
            { label: "Trust Builders", name: "customerExperience.surveys.overallCombinedSurveyScore.trustBuilders", value: data.overallCombinedSurveyScore.trustBuilders || "0" },
            { label: "100% Complete",  name: "customerExperience.surveys.overallCombinedSurveyScore.complete",      value: data.overallCombinedSurveyScore.complete || "0" },
            { label: "On Time:",       name: "customerExperience.surveys.overallCombinedSurveyScore.onTime",         value: data.overallCombinedSurveyScore.onTime || "0" },
            { label: "Customer Rating Score:", name: "customerExperience.surveys.overallCombinedSurveyScore.customerRatingScore", value: data.overallCombinedSurveyScore.customerRatingScore || "0" },
            { label: "Total Avg Score:", name: "customerExperience.surveys.overallCombinedSurveyScore.totalAvgScore", value: data.overallCombinedSurveyScore.totalAvgScore || "0" },
          ].map(field => (
            <div key={field.name} className="grid grid-cols-2 gap-2 items-center">
              <label className="sf-label" style={{ margin: 0 }}>{field.label}</label>
              <input type="text" readOnly name={field.name} value={field.value} className="sf-input-ro px-2 py-1 text-right text-sm" />
            </div>
          ))}
        </div>

        {/* Payout table */}
        <table className="sf-table">
          <thead>
            <tr><th>Survey Score</th><th>% Pay Out</th></tr>
          </thead>
          <tbody>
            {[["5","120%"],["4.8 to 4.9","110%"],["4.6 to 4.7","100%"],["4.4 to 4.5","90%"],["4.2 to 4.3","80%"],["4.0 to 4.1","70%"]].map(([score,payout]) => (
              <tr key={score}><td>{score}</td><td>{payout}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>

    <div className="sf-results grid grid-cols-3 gap-8 mt-4">
      <div>
        <div className="sf-results-label">Bonus % Earned</div>
        <input type="text" readOnly name="customerExperience.surveys.bonusPercentEarned"
          value={data.bonusPercentEarned ? parseFloat(data.bonusPercentEarned).toFixed(0) : "0"}
          className="sf-input-ro w-24 px-2 py-1 text-right text-sm" />
      </div>
      <div>
        <div className="sf-results-label">10%</div>
        <input type="text" readOnly name="customerExperience.surveys.bonusValue"
          value={data.bonusValue ? parseFloat(data.bonusValue).toFixed(2) : "0.10"}
          className="sf-input-ro w-24 px-2 py-1 text-right text-sm" />
      </div>
      <div>
        <div className="sf-results-label">Score</div>
        <input type="text" readOnly name="customerExperience.surveys.score"
          value={data.score && !isNaN(parseInt(data.score)) ? parseInt(data.score).toString() : "0"}
          className="sf-input-ro w-24 px-2 py-1 text-right text-sm" />
      </div>
    </div>
  </div>
);

export default SurveysSubForm;